import { FC } from "react";
import { Typography } from "@kaltura/ds-react-components";
import styled from "@emotion/styled";

import { CardRadio, Variant } from "../card-radio/CardRadio";

/**
 * data structure for room templates
 */
export interface Template {
    /**
     * template entry id
     */
    id: string;
    /**
     * template name (translated)
     */
    name: string;
    /**
     * template description (translated)
     */
    description?: string;
    /**
     * name of illustration component to use for this template.
     * a matching component should exist in the illustrations map.
     * if not defined, a custom illustration will be used
     */
    illustration?: string;
}

/**
 * Props for the TemplateSelection component
 */
export interface TemplateSelectionProps {
    /**
     * Unique identifier for the template selection field
     */
    id: string;

    /**
     * Label text for a single template
     */
    labelSingle: string;

    /**
     * Label text for multiple templates
     */
    labelMultiple: string;

    /**
     * Array of templates to display as options
     */
    templates: Template[];

    /**
     * Map of illustration components keyed by their names
     */
    illustrationsMap: Record<string, FC>;

    /**
     * Default illustration component to use when no specific illustration is defined
     */
    defaultIllustration: FC;

    /**
     * The currently selected template value
     */
    value?: string;

    /**
     * Optional variant to control layout on sm screens
     */
    variant?: Variant;
}

const StyledTemplate = styled(
    "div",
    {
        shouldForwardProp: (prop) => prop !== "variant",
    }
)<{ variant: Variant }>(({ theme, variant }) => ({
    margin: theme.spacing(2),
    display: "flex",
    [theme.breakpoints.down(theme.breakpoints.values.md)]:
        variant === "double"
            ? {
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
              }
            : {},
}));

const StyledTemplateText = styled(
    "div",
    {
        shouldForwardProp: (prop) => prop !== "variant",
    }
)<{ variant: Variant }>(({ theme, variant }) => ({
    [theme.breakpoints.down(theme.breakpoints.values.md)]:
        variant === "double"
            ? {
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
              }
            : {},
}));

const StyledDescription = styled(
    Typography,
    {
        shouldForwardProp: (prop) => prop !== "templateVariant",
    }
)<{ templateVariant: Variant }>(({ theme, templateVariant }) => ({
    color: theme.kaltura.palette.tone2,
    [theme.breakpoints.down(theme.breakpoints.values.md)]:
        templateVariant === "double"
            ? {
                  textAlign: "center",
              }
            : {},
}));

/**
 * Templates component - provides an accessible grid of templates for townhall creation
 */
export const TemplateSelection = (props: TemplateSelectionProps) => {
    const {
        id,
        labelSingle,
        labelMultiple,
        templates,
        illustrationsMap,
        defaultIllustration: DefaultIllustration,
        value,
        variant = "double",
        ...rest
    } = props;

    /**
     * returns the appropriate illustration component based on template data
     */
    const getIllustration = (template: Template) => {
        // if the template defines an illustration, use it.
        if (template.illustration && illustrationsMap[template.illustration]) {
            const Illustration = illustrationsMap[template.illustration];
            return <Illustration />;
        }
        // fallback to a custom illustration if no specific one is defined
        return <DefaultIllustration />;
    };

    return (
        <CardRadio
            {...rest}
            id={id}
            label={templates.length > 1 ? labelMultiple : labelSingle}
            variant={variant}
            cards={templates.map((template: Template, index: number) => {
                return {
                    id: template.id,
                    name: template.name,
                    content: (
                        <StyledTemplate variant={variant}>
                            {getIllustration(template)}
                            <StyledTemplateText variant={variant}>
                                <Typography variant={"body2Highlight"}>{template.name}</Typography>
                                <StyledDescription variant={"body2"} templateVariant={variant}>
                                    {template.description}
                                </StyledDescription>
                            </StyledTemplateText>
                        </StyledTemplate>
                    ),
                };
            })}
            value={value}
        />
    );
};
